/**
 * act-as-cdn-cache.ts — React Query hooks for CDN caching surfaces via act-as.
 *
 * Covers cache config, upstream/origin config, and origin-rewrite config.
 * All mutations use actAsClient so X-Act-As-Org is injected from the active session.
 *
 * Query key factory: actAsCDNCacheKeys.*
 * All GET hooks are enabled-gated by domainId presence.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { actAsClient } from '@/api/act-as-client';

/* ── Types: Cache ────────────────────────────────────────────────────────── */

export interface CacheKeyConfig {
  query_strategy?: string;
  query_params?: string[];
  query_sort?: boolean;
  header_include?: string[];
  cookie_include?: string[];
  device_type?: boolean;
  geo?: boolean;
  lang?: boolean;
}

export interface VaryConfig {
  enabled?: boolean;
  allowlist?: string[];
  normalize_accept_encoding?: boolean;
}

export interface CacheConfig {
  enabled?: boolean;
  bypass_cache?: boolean;
  ignore_client_no_cache?: boolean;
  default_ttl_secs?: number;
  max_ttl_secs?: number;
  edge_ttl_secs?: number;
  browser_ttl_secs?: number;
  stale_while_revalidate_secs?: number;
  stale_if_error_secs?: number;
  cache_key?: CacheKeyConfig | null;
  vary?: VaryConfig | null;
  status_ttl?: Record<string, number>;
}

/* ── Types: Upstream ─────────────────────────────────────────────────────── */

export interface UpstreamOriginUrl {
  value: string;
}

export interface UpstreamOriginHeader {
  name: string;
  value: string;
}

export interface UpstreamConfig {
  mode?: 'http' | 's3';
  urls?: string[];
  selector?: string;
  protocol?: string;
  max_retries?: number;
  connection_pool_size?: number;
  host_header?: string;
  sni_override?: string;
  origin_headers?: UpstreamOriginHeader[];
  tls_to_origin?: boolean;
  origin_cert_id?: string;
  origin_tls_verify?: boolean;
  circuit_breaker_enabled?: boolean;
  circuit_breaker_threshold?: number;
  circuit_breaker_recovery_secs?: number;
  circuit_breaker_half_open?: number;
  origin_shield?: string;
  origin_shield_tls?: boolean;
  retry_on_status?: string;
  retry_on_connect_failure?: boolean;
  retry_on_timeout?: boolean;
  retry_on_reset?: boolean;
  // S3-specific
  s3_service?: string;
  s3_bucket?: string;
  s3_region?: string;
  s3_access_key_id?: string;
  s3_secret_access_key?: string;
  s3_addressing_style?: string;
  s3_endpoint?: string;
}

/** Fan-out envelope returned by GET /cdn/domains/:id/upstream. */
export interface UpstreamFanOut {
  core?: UpstreamConfig | null;
  config?: UpstreamConfig | null;
  coreProvider?: string;
  vendors?: Record<string, { status?: string; error?: string }>;
}

/* ── Types: Origin Rewrite ───────────────────────────────────────────────── */

export interface RewriteRuleOriginHeader {
  name: string;
  value: string;
}

export interface RewriteRule {
  name: string;
  priority: number;
  enabled: boolean;
  match_type: string;
  match_path: string;
  urls: string[];
  weights: number[];
  selector: string;
  host_header: string;
  tls_to_origin: boolean;
  sni_override: string;
  protocol: string;
  origin_headers: RewriteRuleOriginHeader[];
  origin_tls_verify: boolean;
}

export interface OriginRewriteConfig {
  enabled: boolean;
  rules: RewriteRule[];
}

/* ── Query key factory ───────────────────────────────────────────────────── */

export const actAsCDNCacheKeys = {
  cache: (domainId: string) => ['act-as', 'cdn', 'domain', domainId, 'cache'] as const,
  upstream: (domainId: string) => ['act-as', 'cdn', 'domain', domainId, 'upstream'] as const,
  originRewrite: (domainId: string) =>
    ['act-as', 'cdn', 'domain', domainId, 'origin-rewrite'] as const,
};

/* ── Envelope unwrap helper ──────────────────────────────────────────────── */

function unwrap<T>(body: unknown): T | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  // Standard @mgmt envelope: { data: <payload> }
  if ('data' in b && b.data !== null && b.data !== undefined) return b.data as T;
  return b as unknown as T;
}

/* ── Cache hooks ─────────────────────────────────────────────────────────── */

/**
 * GET /api/v3/cdn/domains/:id/cache — current cache configuration.
 * Returns null when the domain has no cache config (404 from BE is normalised).
 */
export function useActAsCDNCache(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNCacheKeys.cache(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<CacheConfig | null> => {
      try {
        const res = await actAsClient.get<unknown>(`/cdn/domains/${domainId}/cache`);
        return unwrap<CacheConfig>(res.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return null;
        throw e;
      }
    },
  });
}

/** PUT /api/v3/cdn/domains/:id/cache */
export function useUpdateActAsCDNCache(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CacheConfig) => {
      const res = await actAsClient.put<unknown>(`/cdn/domains/${domainId}/cache`, payload);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: actAsCDNCacheKeys.cache(domainId) });
    },
  });
}

/** DELETE /api/v3/cdn/domains/:id/cache */
export function useDeleteActAsCDNCache(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await actAsClient.delete(`/cdn/domains/${domainId}/cache`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: actAsCDNCacheKeys.cache(domainId) });
    },
  });
}

/* ── Upstream hooks ──────────────────────────────────────────────────────── */

/**
 * GET /api/v3/cdn/domains/:id/upstream — origin/upstream config.
 * The BE returns a fan-out envelope; we surface the raw body so tabs can decide
 * which slice to use (core vs flat vs vendor map).
 */
export function useActAsCDNUpstreamFull(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNCacheKeys.upstream(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<UpstreamFanOut | null> => {
      try {
        const res = await actAsClient.get<unknown>(`/cdn/domains/${domainId}/upstream`);
        const body = res.data as Record<string, unknown>;
        const inner = (body.data ?? body) as Record<string, unknown>;
        // Fan-out shape: { core, vendors, coreProvider }
        if (inner.core !== undefined || inner.vendors !== undefined) {
          return inner as UpstreamFanOut;
        }
        // Flat config fallback
        if (inner.urls || inner.host_header || inner.mode) {
          return { config: inner as UpstreamConfig, core: inner as UpstreamConfig };
        }
        return null;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return null;
        throw e;
      }
    },
  });
}

/** PUT /api/v3/cdn/domains/:id/upstream */
export function useUpdateActAsCDNUpstream(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpstreamConfig) => {
      const res = await actAsClient.put<unknown>(`/cdn/domains/${domainId}/upstream`, payload);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: actAsCDNCacheKeys.upstream(domainId) });
    },
  });
}

/** DELETE /api/v3/cdn/domains/:id/upstream */
export function useDeleteActAsCDNUpstream(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await actAsClient.delete(`/cdn/domains/${domainId}/upstream`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: actAsCDNCacheKeys.upstream(domainId) });
    },
  });
}

/* ── Origin Rewrite hooks ────────────────────────────────────────────────── */

/**
 * GET /api/v3/cdn/domains/:id/origin-rewrite — path-based origin routing rules.
 * Returns null when not configured.
 */
export function useActAsCDNOriginRewrite(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNCacheKeys.originRewrite(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<OriginRewriteConfig | null> => {
      try {
        const res = await actAsClient.get<unknown>(`/cdn/domains/${domainId}/origin-rewrite`);
        return unwrap<OriginRewriteConfig>(res.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return null;
        throw e;
      }
    },
  });
}

/** PUT /api/v3/cdn/domains/:id/origin-rewrite */
export function useUpdateActAsCDNOriginRewrite(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: OriginRewriteConfig) => {
      const res = await actAsClient.put<unknown>(
        `/cdn/domains/${domainId}/origin-rewrite`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: actAsCDNCacheKeys.originRewrite(domainId) });
    },
  });
}

/** DELETE /api/v3/cdn/domains/:id/origin-rewrite */
export function useDeleteActAsCDNOriginRewrite(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await actAsClient.delete(`/cdn/domains/${domainId}/origin-rewrite`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: actAsCDNCacheKeys.originRewrite(domainId) });
    },
  });
}
